import { useState, useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { useReportDefinition, useViewReport } from '../hooks';
import { DynamicParamForm } from '../components/DynamicParamForm';
import { DataTable } from '@repo/web-list';
import { defineListPage } from '@repo/list';
import { Button } from '../../../components/ui/Button';
import { PageHeader } from '../../../components/layout/PageHeader';
import { formatMoney } from '@repo/utils';
import { Skeleton, TableSkeleton } from '../../../components/ui/Skeleton';
import type { ReportDefinitionDetail } from '../types';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale';
import { DataView } from '../../../components/ui/DataView';

interface ReportViewerPageProps {
  code: string;
}

export function ReportViewerPage({ code }: ReportViewerPageProps) {
  // 1. Fetch Report Definition
  const defQuery = useReportDefinition(code);
  const definition = defQuery.data as ReportDefinitionDetail | undefined;

  // 2. State for Parameters & Form errors
  const [params, setParams] = useState<Record<string, unknown>>({});
  const [activeParams, setActiveParams] = useState<Record<string, unknown> | null>(null);
  const [page, setPage] = useState(1);

  const limit = 20;

  // 3. Fetch Report Data (synchronous JSON in-app viewer)
  const isViewEnabled = activeParams !== null;
  const viewQuery = useViewReport(
    code,
    activeParams ?? {},
    { page, limit },
    { enabled: isViewEnabled }
  );

  // 4. Generate dynamic columns from first data row
  const dataRows = viewQuery.data?.data ?? [];
  const totalCount = viewQuery.data?.meta?.total ?? 0;

  const dynamicColumns = useMemo(() => {
    if (dataRows.length === 0) return [];
    
    const firstRow = dataRows[0];
    const keys = Object.keys(firstRow);
    
    return keys.map((key) => ({
      id: key,
      header: key
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
      accessor: key,
      renderType: 'text' as const,
    }));
  }, [dataRows]);

  // 5. Handle Action Trigger
  function handleGenerate() {
    setActiveParams(params);
    setPage(1);
  }

  // 6. Handle Reset / Change Parameters
  function handleReset() {
    setActiveParams(null);
  }

  if (defQuery.isLoading || defQuery.isError || !definition) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        <div className="space-y-4">
          <Link to="/reports" className="text-sm font-medium text-accent-500 hover:underline flex items-center gap-1 w-fit">
            ← Back to Report Centre
          </Link>
        </div>
        <DataView 
          query={defQuery} 
          loadingSkeleton={
            <div className="space-y-6">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-10 w-64" />
              <Skeleton className="h-48" />
            </div>
          }
        >
          {() => <></>}
        </DataView>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Back navigation & Header */}
      <div className="space-y-4">
        <Link to="/reports" className="text-sm font-medium text-accent-500 hover:underline flex items-center gap-1 w-fit">
          ← Back to Report Centre
        </Link>
        <PageHeader
          title={definition.displayName}
          subtitle={definition.description || undefined}
          className="mb-0"
        />
      </div>

      {/* Parameter Control Panel */}
      <div className="bg-surface-1 border border-border rounded-[12px] p-5 space-y-4">
        <h2 className="text-sm font-semibold text-text-primary uppercase tracking-wider">
          Report Parameters
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="md:col-span-2">
            <DynamicParamForm
              schema={definition.parameterSchema}
              values={params}
              onChange={setParams}
            />
          </div>
          <div className="flex gap-2 justify-end">
            {activeParams && (
              <Button variant="ghost" size="md" onClick={handleReset}>
                Change Parameters
              </Button>
            )}
            <Button variant="primary" size="md" onClick={handleGenerate}>
              {activeParams ? 'Refresh Report' : 'Generate Report'}
            </Button>
          </div>
        </div>
      </div>

      {/* Five-State Data Panel */}
      <div className="bg-surface-1 border border-border rounded-[12px] p-5 min-h-[300px] flex flex-col justify-center">
        {!isViewEnabled ? (
          // State 1: Initial Prompt State
          <div className="text-center py-12 space-y-3 max-w-md mx-auto">
            <div className="h-12 w-12 rounded-full bg-accent-500/10 flex items-center justify-center mx-auto">
              <span className="text-accent-500 text-lg font-bold">i</span>
            </div>
            <h3 className="text-base font-semibold text-text-primary">Ready to Generate</h3>
            <p className="text-xs text-text-secondary">
              Please specify the required parameters above and click <strong>Generate Report</strong> to view the records directly in your browser.
            </p>
          </div>
        ) : (
          <DataView 
            query={viewQuery}
            loadingSkeleton={
              <div className="py-8">
                <TableSkeleton columns={6} rows={8} />
              </div>
            }
            emptyTitle="No Records Found"
            emptyDescription="The report compiled successfully, but no matching records were found for the provided parameter set."
          >
            {() => (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-text-secondary">
                  Showing {dataRows.length} of {totalCount} records
                </h3>
                <DataTable
                  config={defineListPage<any>({ variant: 'data-table', resource: 'report', columns: dynamicColumns as any })}
                  data={dataRows}
                  total={totalCount}
                  page={page}
                  limit={limit}
                  onPageChange={(p) => {
                    setPage(p);
                    // Also trigger update with page/limit in query params of viewReport
                  }}
                  customRenderers={(() => {
                    const isMoneyCol = /(_amount|value|commission|payout|corpus|due|paid|balance|charge|fee|tax|total|sum|price)/i;
                    const renderers: Record<string, (row: Record<string, unknown>) => React.ReactNode> = {};
                    for (const key of dynamicColumns.map(c => c.id)) {
                      renderers[key] = (row: Record<string, unknown>) => {
                        const val = row[key];
                        if (val === null || val === undefined) return <span className="text-text-muted">—</span>;
                        if (typeof val === 'boolean') return val ? 'Yes' : 'No';
                        if (typeof val === 'object') return JSON.stringify(val);
                        if (typeof val === 'number' && isMoneyCol.test(key)) {
                          return formatMoney(val, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE });
                        }
                        return <span className="text-sm text-text-primary">{String(val)}</span>;
                      };
                    }
                    return renderers;
                  })()}
                />
              </div>
            )}
          </DataView>
        )}
      </div>

    </div>
  );
}
